import { toast } from 'react-toastify';
import { ApiError } from '../../types/api';

export interface ErrorHandlerOptions {
  showToast?: boolean;
  customMessage?: string;
  onError?: (error: ApiError) => void;
  silent?: boolean;
}

export class ApiErrorHandler {
  private static readonly ERROR_MESSAGES: Record<number, string> = {
    400: 'Bad request. Please check your input.',
    401: 'Unauthorized. Please login again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'A conflict occurred. The resource may already exist.',
    422: 'Invalid data provided.',
    429: 'Too many requests. Please try again later.',
    500: 'Internal server error. Please try again later.',
    502: 'Bad gateway. Please try again later.',
    503: 'Service unavailable. Please try again later.',
    504: 'Gateway timeout. Please try again later.',
  };

  static handle(error: unknown, options: ErrorHandlerOptions = {}): ApiError {
    const { showToast = true, customMessage, onError, silent = false } = options;
    
    const apiError = this.normalizeError(error);
    
    if (!silent) {
      // Log error in development
      if (process.env.NODE_ENV === 'development') {
        console.error('API Error:', apiError);
      }

      // Show toast notification
      if (showToast) {
        const message = customMessage || this.getErrorMessage(apiError);
        toast.error(message, {
          position: 'top-center',
          autoClose: 5000,
        });
      }
    }

    // Call custom error handler if provided
    if (onError) {
      onError(apiError);
    }

    return apiError;
  }

  static normalizeError(error: unknown): ApiError {
    // RTK Query error
    if (this.isRTKQueryError(error)) {
      return {
        status: error.status as number,
        data: error.data,
        message: this.extractMessageFromRTKError(error),
      };
    }

    // Axios error
    if (this.isAxiosError(error)) {
      return {
        status: error.response?.status,
        data: error.response?.data,
        message: error.response?.data?.message || error.message,
      };
    }

    // Regular Error object
    if (error instanceof Error) {
      return {
        message: error.message,
      };
    }

    // Unknown error
    return {
      message: 'An unexpected error occurred',
    };
  }

  private static getErrorMessage(error: ApiError): string {
    // Use specific error message if available
    if (error.data?.message) {
      return error.data.message;
    }

    // Use generic message based on status code
    if (error.status && this.ERROR_MESSAGES[error.status]) {
      return this.ERROR_MESSAGES[error.status];
    }

    // Use error message if available
    if (error.message) {
      return error.message;
    }

    // Default message
    return 'An unexpected error occurred';
  }

  private static isRTKQueryError(error: unknown): error is { status: number | string; data?: any } {
    return (
      typeof error === 'object' &&
      error !== null &&
      'status' in error
    );
  }

  private static isAxiosError(error: unknown): error is {
    response?: { status?: number; data?: any };
    message: string;
  } {
    return (
      typeof error === 'object' &&
      error !== null &&
      'response' in error
    );
  }

  private static extractMessageFromRTKError(error: any): string | undefined {
    if (error.data?.message) {
      return error.data.message;
    }
    if (error.data?.error) {
      return error.data.error;
    }
    if (typeof error.data === 'string') {
      return error.data;
    }
    return undefined;
  }
}

// Convenience function for handling API errors
export const handleApiError = (error: unknown, options?: ErrorHandlerOptions): ApiError => {
  return ApiErrorHandler.handle(error, options);
};

// Hook for API error handling
import { useCallback } from 'react';

export const useApiErrorHandler = (defaultOptions?: ErrorHandlerOptions) => {
  const handleError = useCallback((error: unknown, options?: ErrorHandlerOptions) => {
    return ApiErrorHandler.handle(error, { ...defaultOptions, ...options });
  }, [defaultOptions]);

  return { handleError };
};